/**
 * Pure predicates behind the Tasks multi-select filters. An empty selection
 * means "no filter on this dimension", matching what "All Priorities" and
 * "All Assignees" did when these were single selects.
 */

export function matchesPriorityFilters(task: { priority?: string }, selected?: string[]): boolean {
  if (!selected || selected.length === 0) return true;
  return !!task.priority && selected.includes(task.priority);
}

export function matchesAssigneeFilters(
  assignees: string[] | undefined,
  selected: string[] | undefined,
  resolveName: (assignee: string) => string,
): boolean {
  if (!selected || selected.length === 0) return true;
  return !!assignees?.some(a => selected.includes(resolveName(a)));
}

/** Order-insensitive equality of two value lists. */
export function isSameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every(v => set.has(v));
}

/**
 * The list's tab / stat-card shortcut plus the default "hide finished" rule.
 * The "hide Done" fallback only applies when the Status filter is empty: once a
 * Status selection exists it alone decides which statuses are shown, so
 * ticking Done really shows Done tasks.
 */
export function matchesTab(
  task: { priority?: string; status?: string; blockedOn?: unknown },
  tab: string,
  opts: { hasStatusFilter: boolean; isOverdue: (t: any) => boolean },
): boolean {
  if (tab === 'Done') return task.status === 'Done';
  if (tab === 'Overdue') return opts.isOverdue(task);
  if (tab === 'P0/P1') return task.priority === 'P0' || task.priority === 'P1';
  if (tab === 'Blocked') return !!task.blockedOn;
  // 'All' and 'Outstanding': the default view.
  return opts.hasStatusFilter ? true : task.status !== 'Done';
}

/** Stat-card shortcuts that narrow the list; they are shown as a filter chip and cleared by Clear all. */
export const TAB_SHORTCUTS = ['Overdue', 'P0/P1', 'Blocked'];
